import { FC, createRef, useEffect, useMemo, useRef, useState } from 'react';
import { Card, Space, Typography } from 'antd';
import { Navigate, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useMediaQuery } from 'usehooks-ts';
import { ProcInstanceContext, UserProcStepContext } from '@/entities/proc';
import { ProcsComponentsRenderer } from '@/features/proc-management';
import {
  ProcDetailsResponse,
  StepItemResponse,
  useProcDetails,
} from '@/shared/api';
import { useThemeContext, themeConfig } from '@/shared/theme';
import { ChatMode, ComponentMode as Mode } from '@/shared/types';
import { Row, Tags } from '@/shared/ui';
import { InstanceStepChat } from '@/widgets/chat';
import { PageLayout, PageRefProps } from '@/widgets/page-layout';
import { StepsSidebar } from '@/widgets/steps-sidebar';
import { PublishedProcViewPageHeader } from './PublishedProcViewPageHeader';

type Props = {
  procId: string;
};

const { Title } = Typography;

export const PublishedProcView: FC<Props> = ({ procId }) => {
  const { theme } = useThemeContext();
  const navigate = useNavigate();
  const isMobileView = useMediaQuery('(max-width: 768px)');

  const containerRef = useRef(null);
  const pageRef = useRef<PageRefProps>(null);
  const sidebarRef = useRef(null);

  const [selectedStepId, setSelectedStepId] = useState<string>();

  const { data: procData, error } = useProcDetails<ProcDetailsResponse>(
    {
      pathParams: { id: procId },
    },
    {
      onError: (error: any) => {
        if (
          error.stack.statusCode === 400 ||
          error.stack.statusCode === 403 ||
          error.stack.statusCode === 404
        ) {
          navigate('/dashboard/404', { replace: true });
        }
      },
    },
  );

  const stepRefs = useMemo(
    () =>
      procData?.stepData.steps.reduce((acc, value) => {
        acc[value.id] = createRef<HTMLDivElement>();
        return acc;
      }, {}),
    [procData?.stepData.steps],
  );

  const handleStepSelect = (id: string) => {
    stepRefs &&
      stepRefs[id as keyof typeof stepRefs].current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    setSelectedStepId(id);
    isMobileView && pageRef.current?.closeLeftSidebar();
  };

  useEffect(() => {
    if (procData?.stepData.steps.length === 0) return;

    let timer: any = null;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (timer !== null) {
              clearTimeout(timer);
            }
            timer = setTimeout(() => {
              const index = entry.target.dataset.index;
              setSelectedStepId(procData?.stepData.steps[index!].id);
            }, 250);
          }
        });
      },
      { threshold: 0.5 },
    );

    const items = containerRef.current!.querySelectorAll('[data-index]');
    items.forEach((item: any) => observer.observe(item));

    return () => {
      items.forEach((item: any) => observer.unobserve(item));
    };
  }, [procData]);

  if (error as any) {
    const errorData = error as any;
    const errorCode = errorData?.stack?.error;
    if (errorCode === 'ResourceNotFound') {
      return <Navigate to="/404" />;
    }
  }

  return (
    <ProcInstanceContext.Provider
      value={{ procInstance: procData, selectedStepId, handleStepSelect }}
    >
      <PageLayout
        ref={pageRef}
        headerContent={<PublishedProcViewPageHeader proc={procData} />}
        contentStyle={{
          paddingTop: 20,
          backgroundColor: themeConfig[theme].colors.bgBase,
        }}
        sidebarLeftContent={
          <StepsSidebar
            ref={sidebarRef}
            steps={procData?.stepData.steps || []}
            selectedStepId={selectedStepId}
            onStepSelect={handleStepSelect}
          />
        }
      >
        {Boolean(procData?.imageLink) && (
          <div
            style={{
              backgroundImage: `url(${procData?.imageLink})`,
              backgroundSize: 'cover',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
              height: '100%',
              width: '100%',
              maxWidth: 1200,
              maxHeight: '60%',
              margin: '0 auto',
              display: 'block',
              marginBottom: 40,
              borderRadius: 10,
            }}
          />
        )}
        {Boolean(procData?.uploadedImageLink) && (
          <div
            style={{
              backgroundImage: `url(${procData?.uploadedImageLink})`,
              backgroundSize: 'cover',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
              height: '100%',
              width: '100%',
              maxWidth: 1200,
              maxHeight: '60%',
              margin: '0 auto',
              display: 'block',
              marginBottom: 40,
              borderRadius: 10,
            }}
          />
        )}
        <div
          ref={containerRef}
          style={{
            width: '100%',
            maxWidth: 1000,
            margin: '0 auto',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <div style={{ width: 'fit-content' }}>
            <Title
              style={{
                textAlign: 'center',
                margin: '20px 0 50px',
              }}
            >
              {procData?.title}
            </Title>
            {Boolean(procData?.description) && (
              <div
                dangerouslySetInnerHTML={{
                  __html: procData?.description as any,
                }}
                style={{
                  lineHeight: 1.8,
                  color: themeConfig[theme].colors.text,
                }}
              />
            )}
            <Row gap={0} style={{ marginTop: 20, marginBottom: 40 }}>
              <Tags tags={procData?.tags} />
            </Row>
          </div>
          {procData?.stepData.steps.map(
            (step: StepItemResponse, index: number) => (
              <div
                key={step.id}
                ref={stepRefs && stepRefs[step.id as keyof typeof stepRefs]}
                data-index={index}
                style={{
                  padding: '20px 0',
                  width: '100%',
                  maxWidth: 689,
                  wordBreak: 'break-word',
                }}
              >
                <Card
                  title={
                    <Row style={{ gap: 8 }}>
                      <StepNumber
                        style={{
                          color:
                            theme === 'light'
                              ? themeConfig[theme].colors.text
                              : themeConfig[theme].colors.bgBase,
                        }}
                      >
                        #{index + 1}
                      </StepNumber>{' '}
                      {step.title}
                    </Row>
                  }
                >
                  <UserProcStepContext.Provider value={{ stepId: step.id }}>
                    <Space
                      direction="vertical"
                      size="large"
                      style={{ width: '100%' }}
                    >
                      {step.components.map((block: any, index: number) =>
                        ProcsComponentsRenderer({
                          block,
                          index,
                          mode: Mode.Preview,
                        }),
                      )}
                      {(step.commentsEnabled ||
                        procData.stepData.administration
                          .aiAssistantEnabled) && (
                        <div style={{ marginTop: 10 }}>
                          <InstanceStepChat
                            chatMode={ChatMode.Preview}
                            step={step}
                            label={step.commentsLabel}
                            postedBy="userAccount"
                          />
                        </div>
                      )}
                    </Space>
                  </UserProcStepContext.Provider>
                </Card>
              </div>
            ),
          )}
        </div>
      </PageLayout>
    </ProcInstanceContext.Provider>
  );
};

const StepNumber = styled.div`
  font-family: 'Inter', sans-serif !important;
  background: #ffed66;
  padding: 5px;
  border-radius: 5px;
  font-weight: 500;
  font-size: 11px;
  max-height: 19px;
  display: flex;
  align-items: center;
  justify-content: center;
`;
